import { NotificationProvider, PageSkeleton } from '@sellsen/components';
import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { App } from './app';
import { RequireSellerAccess } from './components/require-seller-access';
import { RequireStoreValue } from './components/require-store-value';
import { RequireAdmin } from './components/require-admin';

// Lazy load pages
const DashboardPage = lazy(() => import('./pages/dashboard'));
const StoreFrontPage = lazy(() => import('./pages/storefront'));
const SearchByKeywordPage = lazy(() => import('./pages/search-by-keyword'));
const AsinPool = lazy(() => import('./pages/asin-pool'));
const PricingPage = lazy(() => import('./pages/pricing'));
const CurrencyExchangePage = lazy(() => import('./pages/currency-exchange'));
const MarketPlaceInfoPage = lazy(() => import('./pages/marketplace-info'));
const ProductSearchPage = lazy(() => import('./pages/product-search'));
const RestrictionCheckPage = lazy(() => import('./pages/restriction-check'));
const ProductMarketLocationSelectPage = lazy(
  () => import('./pages/product-market-select')
);
const SettingsPage = lazy(() => import('./pages/settings'));
const SupportRequestPage = lazy(() => import('./pages/support-request'));
const WarehouseCasesPage = lazy(() => import('./pages/warehouse-cases'));
const WarehouseCreditPage = lazy(() => import('./pages/warehouse-credit'));
const WarehouseInfoPage = lazy(() => import('./pages/warehouse-info'));
const NewMarketPlacePage = lazy(() => import('./pages/new-marketplace'));
const Login = lazy(() => import('./pages/login'));
const AutoOrderHandoffPage = lazy(() => import('./pages/autoorder'));
const LogoutPage = lazy(() => import('./pages/logout'));
const OAuthManagementPage = lazy(() => import('./pages/oauth-management'));
const OAuthRedirectPage = lazy(() => import('./pages/oauth/redirect'));
const ProductStagingPage = lazy(() => import('./pages/product-staging'));
const BannedCategoriesPage = lazy(() => import('./pages/banned-categories'));
const BannedBrandsPage = lazy(() => import('./pages/banned-brands'));
const BannedASINPage = lazy(() => import('./pages/banned-asin'));
const BannedKeywordPage = lazy(() => import('./pages/banned-keyword'));
const BannedCommonBrandsPage = lazy(
  () => import('./pages/banned-common-brands')
);
const BannedCommonAsinsPage = lazy(() => import('./pages/banned-common-asins'));
const BannedCommonCategoriesPage = lazy(
  () => import('./pages/banned-common-categories')
);
const BannedCommonKeywordsPage = lazy(
  () => import('./pages/banned-common-keywords')
);
const InventoriesPage = lazy(() => import('./pages/inventories'));
const InventoryUpdateHistoryPage = lazy(
  () => import('./pages/inventory-update-history')
);
const PromotionsPage = lazy(() => import('./pages/promotions'));
const BusinessReportsPage = lazy(() => import('./pages/business-reports'));
const MarketplaceFilterSetting = lazy(
  () => import('./pages/marketplace-filter-setting')
);
const OrdersListPage = lazy(() => import('./pages/orders'));
const OrderDetailPage = lazy(() => import('./pages/orders/detail'));
const OrderDashboardPage = lazy(() => import('./pages/order-dashboard'));
const DailyStatsPage = lazy(() => import('./pages/daily-stats'));
const OrderTrackingPage = lazy(() => import('./pages/order-tracking'));
const WarehousePackagesPage = lazy(() => import('./pages/warehouse/packages'));
const WarehousePackageDetailPage = lazy(
  () => import('./pages/warehouse/packages/detail')
);

// Suspense wrapper for cleaner fallback
const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<PageSkeleton />}>
    <Component />
  </Suspense>
);

const withNotification = (Component: React.ComponentType) => (
  <Suspense fallback={<PageSkeleton />}>
    <NotificationProvider>
      <Component />
    </NotificationProvider>
  </Suspense>
);

export const routes = createBrowserRouter([
  {
    path: '/',
    Component: App,
    children: [
      // Herkese açık (ROLE_USER dahil): dashboard, Asin Yönetimi, araçlar, profil.
      { index: true, element: withSuspense(DashboardPage) },
      { path: '/search-by-store', element: withSuspense(StoreFrontPage) },
      {
        path: '/search-by-keyword',
        element: withSuspense(SearchByKeywordPage),
      },
      { path: '/asin-pool', element: withSuspense(AsinPool) },
      {
        path: '/currency-exchange',
        element: withSuspense(CurrencyExchangePage),
      },
      {
        path: '/marketplace-info',
        element: withSuspense(MarketPlaceInfoPage),
      },
      { path: '/settings', element: withSuspense(SettingsPage) },
      { path: '/support-request', element: withSuspense(SupportRequestPage) },
      // Satıcı modülleri (ROLE_SELLER / ROLE_ADMIN).
      {
        element: <RequireSellerAccess />,
        children: [
          {
            path: '/pricing',
            element: (
              <RequireStoreValue>{withSuspense(PricingPage)}</RequireStoreValue>
            ),
          },
          {
            path: '/product-search',
            element: (
              <RequireStoreValue>
                {withSuspense(ProductSearchPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/restriction-checks',
            element: (
              <RequireStoreValue>
                {withSuspense(RestrictionCheckPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/product-market-select',
            element: (
              <RequireStoreValue>
                {withSuspense(ProductMarketLocationSelectPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/new-marketplace',
            element: withSuspense(NewMarketPlacePage),
          },
          {
            path: '/oauth-management',
            element: withSuspense(OAuthManagementPage),
          },
          { path: '/oauth/spapi', element: withSuspense(OAuthRedirectPage) },
          {
            path: '/oauth/spapi/boost',
            element: withSuspense(OAuthRedirectPage),
          },
          {
            path: '/product-staging',
            element: (
              <RequireStoreValue>
                {withSuspense(ProductStagingPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/banned/categories',
            element: (
              <RequireStoreValue>
                {withSuspense(BannedCategoriesPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/banned/brands',
            element: (
              <RequireStoreValue>
                {withSuspense(BannedBrandsPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/banned/asins',
            element: (
              <RequireStoreValue>
                {withSuspense(BannedASINPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/banned/keywords',
            element: withSuspense(BannedKeywordPage),
          },
          {
            path: '/banned/common-brands',
            element: (
              <RequireStoreValue>
                {withSuspense(BannedCommonBrandsPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/banned/common-asins',
            element: (
              <RequireStoreValue>
                {withSuspense(BannedCommonAsinsPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/banned/common-categories',
            element: (
              <RequireStoreValue>
                {withSuspense(BannedCommonCategoriesPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/banned/common-keywords',
            element: (
              <RequireStoreValue>
                {withSuspense(BannedCommonKeywordsPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/inventories',
            element: (
              <RequireStoreValue>
                {withSuspense(InventoriesPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/inventory-update-history',
            element: (
              <RequireStoreValue>
                {withSuspense(InventoryUpdateHistoryPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/promotions',
            element: (
              <RequireStoreValue>
                {withSuspense(PromotionsPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/business-reports',
            element: (
              <RequireStoreValue>
                {withSuspense(BusinessReportsPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/marketplace-filter-settings',
            element: (
              <RequireStoreValue>
                {withSuspense(MarketplaceFilterSetting)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/orders',
            element: (
              <RequireStoreValue>
                {withSuspense(OrdersListPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/order-dashboard',
            element: (
              <RequireStoreValue>
                {withSuspense(OrderDashboardPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/order-tracking',
            element: withSuspense(OrderTrackingPage),
          },
          {
            path: '/orders/:amazonOrderId',
            element: (
              <RequireStoreValue>
                {withSuspense(OrderDetailPage)}
              </RequireStoreValue>
            ),
          },
          {
            path: '/daily-stats',
            element: withSuspense(DailyStatsPage),
          },
          {
            path: '/warehouse-credit',
            element: withSuspense(WarehouseCreditPage),
          },
          {
            path: '/warehouse-cases',
            element: withSuspense(WarehouseCasesPage),
          },
          {
            path: '/warehouse-info',
            element: withSuspense(WarehouseInfoPage),
          },
        ],
      },
      // Depo paneli: tüm kullanıcıların paketleri, yalnız admin.
      {
        path: '/warehouse',
        element: <RequireAdmin />,
        children: [
          { index: true, element: <Navigate to="packages" replace /> },
          {
            path: 'packages',
            element: withSuspense(WarehousePackagesPage),
          },
          {
            path: 'packages/:warehousePackageId',
            element: withSuspense(WarehousePackageDetailPage),
          },
        ],
      },
    ],
  },
  // Eklentiye devir sayfası: auth guard'ının DIŞINDA, çünkü oturum yönlendirmesi URL'deki
  // satın alma parametrelerini eklenti okumadan siliyor.
  { path: '/autoorder', element: withSuspense(AutoOrderHandoffPage) },
  { path: '/login', element: withNotification(Login) },
  { path: '/logout', element: withSuspense(LogoutPage) },
]);
